import React from 'react';
import { Calendar, Users, GraduationCap, Rocket } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import CTASection from '../components/CTASection';
import { SEOHead, createBreadcrumbSchema } from '../components/SEOHead';

const HowItWorksPage = () => {
  const steps = [
    {
      icon: Calendar,
      number: '01',
      title: 'Webinář nebo konzultace s naším odborníkem',
      description: 'Ukážeme vám, jak Školobot funguje v praxi, co všechno ve škole zvládne a kde pedagogům skutečně šetří čas. Společně se podíváme na typické situace ze školní praxe — od přípravy výuky přes IVP a hodnocení žáků až po administrativu.',
      cta1: 'Webinář zdarma',
      cta2: 'Vyzkoušet zdarma',
      bg: 'from-blue-50 to-cyan-50'
    },
    {
      icon: Users,
      number: '02',
      title: 'Specifikace počtu pedagogů a nákup licencí',
      description: 'Vyspecifikujete počet pedagogů, kteří budou Školobota využívat. Nákup probíhá standardně s platbou na fakturu. Školobot je koncipovaný v souladu s metodickým výkladem MŠMT „Digitální technologie jako učební pomůcka" (edu.gov.cz). Jeho pořízení tak lze za splnění podmínek této metodiky hradit z veřejných prostředků jako učební pomůcku podle § 160 školského zákona.',
      footer: 'Fakturace, možnost úhrady z veřejných prostředků',
      bg: 'from-purple-50 to-pink-50'
    },
    {
      icon: GraduationCap,
      number: '03',
      title: 'Onboarding pedagogů',
      description: 'Pro každou funkcionalitu Školobota máme připravené videonávody, které ukáží pedagogům krok za krokem, jak dané nástroje využít v praxi. Každý pedagog si může projít návody ve svém tempu a postupně se naučit všechny funkce.',
      footer: 'Videonávody, krok za krokem, vlastní tempo',
      bg: 'from-green-50 to-emerald-50'
    },
    {
      icon: Rocket,
      number: '04',
      title: 'Plné nasazení',
      description: 'Školobot se stává přirozenou součástí práce celé školy. Pedagogové ho využívají napříč výukou i administrativou, sdílejí materiály a pracují v jednotném prostředí, které škole otevírá cestu k nové generaci vzdělávání.',
      footer: 'Jedna faktura, sdílená knihovna, dedikovaná podpora.',
      bg: 'from-orange-50 to-amber-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[color:var(--bg)] to-white">
      <SEOHead 
        title="Jak začít | Školobot pro školy"
        description="Jak implementovat Školobot ve vaší škole. Webinář, pilotní projekt, školení pedagogů a plné nasazení AI nástroje."
        path="/jak-zacit"
        schema={createBreadcrumbSchema([{ name: 'Úvod', path: '/' }, { name: 'Jak začít', path: '/jak-zacit' }])}
      />
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h1 className="text-4xl md:text-5xl font-bold text-[color:var(--text-primary)] mb-4">
              Jak zavést Školobota do vaší školy
            </h1>
            <p className="text-lg text-[color:var(--text-secondary)]">
              Od první ukázky až po spuštění pro pedagogy — jednoduše a bez zbytených komplikací
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <FadeIn key={index} delay={index * 0.1}>
                  <div className={`bg-gradient-to-br ${step.bg} rounded-3xl p-8 md:p-10 border-2 border-white shadow-lg`}>
                    <div className="flex flex-col md:flex-row items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center shadow-md">
                            <Icon className="w-10 h-10 text-[color:var(--primary-600)]" />
                          </div>
                          <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-[color:var(--primary-500)] text-white font-bold flex items-center justify-center text-lg shadow-lg">
                            {step.number}
                          </div>
                        </div>
                      </div>

                      <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold text-[color:var(--text-primary)] mb-3">
                          {step.title}
                        </h3>
                        <p className="text-base text-[color:var(--text-secondary)] mb-4 leading-relaxed">
                          {step.description}
                        </p>
                        {step.footer && (
                          <p className="text-sm text-[color:var(--primary-600)] font-semibold">
                            {step.footer}
                          </p>
                        )}
                        {step.cta1 && (
                          <div className="flex gap-3 mt-6">
                            <a href="/#webinar">
                              <button className="bg-[color:var(--primary-500)] hover:bg-[color:var(--primary-600)] text-white px-6 py-3 rounded-xl font-semibold transition-colors">
                                {step.cta1}
                              </button>
                            </a>
                            {step.cta2 && (
                              <a href="https://app.skolobot.cz/register" target="_blank" rel="noopener noreferrer">
                                <button className="bg-white border-2 border-[color:var(--primary-500)] text-[color:var(--primary-600)] px-6 py-3 rounded-xl font-semibold hover:bg-[color:var(--primary-50)] transition-colors">
                                  {step.cta2}
                                </button>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default HowItWorksPage;